import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { AppleShare } from '@/components_deprecated/icons/icons'
import { addMonths } from 'date-fns'
import type { MouseEventHandler } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { isIOS } from 'react-device-detect'
import styled from 'styled-components'
import { useLocalStorage, useMedia } from '@/utils/hooks'

export const isInStandalone = () =>
  (window.navigator as any).standalone || // iOS flag
  window.matchMedia('(display-mode: standalone)').matches

interface PromptEvent extends Event {
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
  prompt: () => Promise<void>
}

export default function useAddToHomeScreenPrompt() {
  const [deferred, setDeferred] = useState<PromptEvent | null>(null)

  useEffect(() => {
    const handler = (evt: Event) => {
      evt.preventDefault() // stop Chrome’s mini-infobar
      setDeferred(evt as PromptEvent) // cache the event
    }
    window.addEventListener('beforeinstallprompt', handler)

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const prompt = useCallback(async () => {
    if (!deferred) return false

    void deferred.prompt()

    const { outcome } = await deferred.userChoice
    setDeferred(null) // event can be used only once

    return outcome === 'accepted'
  }, [deferred])

  return { canPrompt: !!deferred, prompt }
}

const Banner = styled.aside`
  grid-column: 1 / -1;

  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;

  padding: 1rem;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};

  p {
    grid-column: 1;
    margin: 0;
    line-height: 1.5;
    text-wrap: pretty;

    img {
      display: inline;
      width: 1em;
      fill: currentColor;
    }
    strong {
      white-space: nowrap;
      display: inline-flex;
      align-items: center;
    }
  }
  div.close {
    grid-column: 2;
  }
  div.ios-overlay {
    grid-column: 1 / -1;
  }
`

const LS_ITEM_KEY = 'hide-homescreen-prompt-until'

function transformLSItem(item: string) {
  const num = parseInt(item, 10)
  if (isNaN(num)) return null

  return num
}

export const HomescreenPrompt: React.JSX.CFC = () => {
  const translations = useTranslate({
    addToHomescreen: 'login.add-to-homescreen.cta',
    iosHelp: [
      'login.add-to-homescreen.ios-help',
      {
        share: <strong />,
        button: <strong />,
      },
    ],
    share: 'login.add-to-homescreen.share',
    iosButton: 'login.add-to-homescreen.ios-button',
  })

  const [lsItem, setLSItem] = useLocalStorage(
    LS_ITEM_KEY,
    transformLSItem,
    null
  )

  const [showIosHelp, setShowIosHelp] = useState(false)

  const { canPrompt, prompt } = useAddToHomeScreenPrompt()
  const [mobile] = useMedia()

  const hide = useMemo(() => {
    if (!mobile || isInStandalone()) return true
    if (!lsItem) return false

    const now = new Date()
    return now.getTime() < lsItem
  }, [lsItem, mobile])

  const handleClose = useCallback<MouseEventHandler<HTMLDivElement>>(
    evt => {
      evt.stopPropagation()
      // Hide for one month
      const date = addMonths(new Date(), 1)
      setLSItem(date.getTime())
    },
    [setLSItem]
  )

  const handleClick = useCallback(async () => {
    if (showIosHelp) {
      setShowIosHelp(false)
      return
    }

    if (!isIOS && canPrompt) await prompt()
    else if (isIOS) setShowIosHelp(true)
  }, [canPrompt, prompt, showIosHelp])

  // Not mobile or already installed
  if (hide) return null

  return (
    <Banner
      role="button"
      aria-label="Add to Home Screen"
      onClick={() => handleClick()}
    >
      <p className="cta">{translations.addToHomescreen}</p>

      <div
        className="close"
        role="button"
        aria-label="Hide this prompt"
        onClick={handleClose}
      >
        <Icon icon="times" size="2rem" color="white" />
      </div>

      {showIosHelp && (
        <div className="ios-overlay">
          <p>
            {translations.iosHelp({
              share: (
                <strong>
                  {translations.share}&nbsp;
                  <AppleShare
                    fill="currentColor"
                    width="1.2em"
                    height="1.2em"
                  />
                </strong>
              ),
              button: <strong>{translations.iosButton}</strong>,
            })}
          </p>
        </div>
      )}
    </Banner>
  )
}
